import Emitter from '../../../shared/emitter'

export interface AjaxResponse {
  status: number
  body?: string
}

export interface AjaxRequest {
  method: string
  url: string
  response?: Promise<AjaxResponse>
}

export interface AjaxRequestFilter {
  method?: string
  url?: string
}

const originalOpen = (window as any).XMLHttpRequest.prototype.open

function newOpen(method: string, url: string, ...rest: unknown[]) {
  if (watcher) {
    const onreadystatechange = this.onreadystatechange
    this.onreadystatechange = (...args: unknown[]) => {
      if (this.readyState === XMLHttpRequest.DONE) {
        if (watcher) {
          try {
            const response = Promise.resolve({ body: this.responseText, status: this.status })
            watcher.trigger('request', { method, url, response })
          } catch (e) {
            console.warn('failed to handle ajax event', e)
          }
        }
      }
      if (onreadystatechange) {
        onreadystatechange.apply(this, args)
      }
    }
  }
  return originalOpen.apply(this, [method, url, ...rest])
}

const originalFetch = (window as any).fetch

function newFetch(...args: unknown[]) {
  const promise = originalFetch.apply(this, args)
  if (promise && typeof promise.then === 'function') {
    const fetchArgs = args
    promise
      .then((resp: Response) => {
        if (watcher) {
          try {
            let ajaxRequest: AjaxRequest
            const response = resp
              .clone()
              .text()
              .then((body) => {
                return { status: resp.status, body }
              })
            if (fetchArgs.length === 1) {
              const reqInfo = fetchArgs[0] as RequestInfo
              ajaxRequest =
                typeof reqInfo === 'string'
                  ? { method: 'GET', url: reqInfo, response }
                  : { method: reqInfo.method, url: reqInfo.url, response }
            } else if (fetchArgs.length === 2) {
              const reqInit = fetchArgs[1] as RequestInit
              ajaxRequest = { method: reqInit.method, url: fetchArgs[0] as string, response }
            }
            if (ajaxRequest) {
              watcher.trigger('request', ajaxRequest)
            }
          } catch (e) {
            console.warn('failed to handle fetch event', e)
          }
        }
      })
      .catch(() => {
        // silence possible fetch errors, such as 'AbortError'
      })
  }
  return promise
}

let isInitialized = false

function initialize() {
  if (!isInitialized) {
    ;(window as any).XMLHttpRequest.prototype.open = newOpen
    ;(window as any).fetch = newFetch
    isInitialized = true
  }
}

let watcher: Emitter

function getAjaxWatcher(): Emitter {
  if (!watcher) {
    watcher = new Emitter()
  }
  return watcher
}

export function testAjaxRequest(req: AjaxRequest, filter: AjaxRequestFilter): boolean {
  if (filter.method && req.method && filter.method.toLowerCase() !== req.method.toLowerCase()) {
    return false
  }
  if (filter.url && req.url && !req.url.match(filter.url)) {
    return false
  }
  return true
}

export function ajaxWatcher(callback: (req: AjaxRequest) => void, filter?: AjaxRequestFilter) {
  initialize()
  getAjaxWatcher().on('request', (_, req: AjaxRequest) => {
    if (testAjaxRequest(req, filter) && callback) {
      setTimeout(() => callback(req), 0)
    }
  })
}
